// Used guide from here:
// https://codeburst.io/vuex-and-typescript-3427ba78cfa8
//
import { Record } from "../common/api/leaderboard/Record";
import { Module, ActionTree, MutationTree } from "vuex";
import { RootState } from "./store";
import {
  ListResponse,
  ListResponseRecord
} from "@/common/api/leaderboard/List";
import {
  PostScoreRequest,
  PostScoreResponse
} from "@/common/api/leaderboard/PostScore";
import { request } from "./request";

// Helpers for type safety
export namespace Actions {
  export const FETCH: string = "fetch";
  export const POST_SCORE: string = "postScore";
}

export namespace Mutations {
  export const SET_USERS: string = "setUsers";
  export const SET_CURRENT_RECORD: string = "setCurrent";
  export const SET_SUBMITTED_RECORD: string = "setSubmitted";
  export const SET_ERROR: string = "setError";
}

/** The base URL for the leaderboard information. */
const url: string = "/api/leaderboard";

type PostedRecord = Record & { submission?: boolean };

/** The leaderboard state */
export interface LeaderboardState {
  lastRefresh: number;
  currentScore?: Record;
  users: Record[];
  posted: PostedRecord | undefined;
  error: boolean;
}

const state: LeaderboardState = {
  lastRefresh: -1,
  currentScore: { initials: "", score: 0 },
  users: [],
  posted: undefined,
  error: false
};

const namespaced: boolean = true;

/** The actions to perform */
const actions: ActionTree<LeaderboardState, RootState> = {
  /**
   * Fetches a fresh leaderboard
   */
  [Actions.FETCH](context) {
    return request<void, ListResponse>(`${url}/list`, "GET").then(
      (res: ListResponse) => {
        context.commit(Mutations.SET_USERS, res);
        return res;
      }
    );
  },

  /**
   * Action to post a high score to the server.
   * @param context
   * @param payload
   */
  async [Actions.POST_SCORE](
    context,
    payload: PostScoreRequest
  ): Promise<PostScoreResponse> {
    try {
      const result = await request<PostScoreRequest, PostScoreResponse>(
        `${url}/post`,
        "POST",
        payload
      );

      context.commit(Mutations.SET_SUBMITTED_RECORD, {
        ...result.record,
        submission: true
      });
      return result;
    } catch (err) {
      throw err;
    }
  }
};

const mutations: MutationTree<LeaderboardState> = {
  [Mutations.SET_CURRENT_RECORD](state, payload: Record) {
    state.currentScore = payload;
  },
  /**
   * Sets the list of users
   */
  [Mutations.SET_USERS](state, payload: ListResponse) {
    state.users = payload.users;
  },

  /**
   * Sets the list of users
   */
  [Mutations.SET_SUBMITTED_RECORD](
    state,
    payload: Record & { submission: boolean }
  ) {
    state.posted = payload;
  },

  /**
   * Sets the state.
   */
  [Mutations.SET_ERROR](state: LeaderboardState, payload: boolean) {
    state.error = payload;
  }
};

const getters = {
  combined(state: LeaderboardState) {
    if (state.posted) {
      const newstate = state.users.slice();
      newstate.forEach((item: PostedRecord) => {
        if (
          item.initials == state.posted!.initials &&
          item.score == state.posted!.score
        ) {
          item.submission = true;
        }
      });

      // newstate.push(state.posted);
      return newstate;
    }
    return state.users;
  }
};

export const leaderboard: Module<LeaderboardState, RootState> = {
  namespaced,
  state,
  actions,
  mutations,
  getters
};
